import { AutoJoinLists } from 'common/Prose/Plugins/AutoJoinLists'
import { CleanUpZeroWidthSpaces } from 'common/Prose/Plugins/CleanUpZeroWidthSpaces'
import { CoerceLists } from 'common/Prose/Plugins/CoerceLists'
import { ExtendSelection } from 'common/Prose/Plugins/ExtendSelection/extendSelection'
import { FakeSelection } from 'common/Prose/Plugins/FakeSelection'
import { FloatingMenuAnchorPlugin } from 'common/Prose/Plugins/FloatingMenuAnchorPlugin/FloatingMenuAnchorPlugin'
import { BorderFormatBarPlugin } from 'common/Prose/Plugins/FormatBars/BorderFormatBarPlugin/BorderFormatBarPlugin'
import { CellFormatBarPlugin } from 'common/Prose/Plugins/FormatBars/CellFormatBarPlugin/CellFormatBarPlugin'
import { TableFormatBarPlugin } from 'common/Prose/Plugins/FormatBars/TableFormatBarPlugin/TableFormatBarPlugin'
import { gapCursor } from 'common/Prose/Plugins/GapCursor'
import { InclusiveCodeMarks } from 'common/Prose/Plugins/InclusiveCodeMarks'
import { InlineCodeCursor } from 'common/Prose/Plugins/InlineCodeCursor'
import { JoinLists } from 'common/Prose/Plugins/JoinLists'
import { KeepMarks } from 'common/Prose/Plugins/KeepMarks'
import { Keymap } from 'common/Prose/Plugins/Keymap'
import { OpenLinkInNewTab } from 'common/Prose/Plugins/OpenLinkInNewTab'
import { PreserveListItemOnPaste } from 'common/Prose/Plugins/PreserveListItemOnPaste'
import { SpreadPasteIntoListItems } from 'common/Prose/Plugins/SpreadPasteIntoListItems'
import { InputRules } from 'common/Prose/Plugins/inputRules'
import { ListItemMarkerSelection } from 'common/Prose/Plugins/listItemMarkerSelection'
import { tableSelection } from 'common/Prose/Plugins/tableSelection/tableSelection'
import { BlockIdPlugin } from 'proseShared/plugins/BlockId/BlockIdPlugin'
import { CommentsPlugin } from 'proseShared/plugins/Comments/Comments'
import { ElementPresencePlugin } from 'proseShared/plugins/ElementPresence/ElementPresence'
import EmojiInputPlugin from 'proseShared/plugins/EmojiInput/EmojiInput'
import EmptyPrompt from 'proseShared/plugins/EmptyPrompt/EmptyPrompt'
import { InlineLinkMenuPlugin } from 'proseShared/plugins/InlineLinkMenu/InlineLinkMenu'
import { InlinePresencePlugin } from 'proseShared/plugins/InlinePresence/InlinePresence'
import MentionPromptPlugin from 'proseShared/plugins/MentionPrompt/MentionPrompt'
import { MentionsPlugin } from 'proseShared/plugins/Mentions/Mentions'
import { PasteImagePlugin } from 'proseShared/plugins/PasteImage/PasteImagePlugin'
import { PastePlugin } from 'proseShared/plugins/PastePlugin/PastePlugin'
import { ScrollToHashOnSyncFinished } from 'proseShared/plugins/ScrollToHashOnSyncFinished/ScrollToHashOnSyncFinished'
import SnippetsPlugin from 'proseShared/plugins/Snippets/Snippets'
import { TodosPlugin } from 'proseShared/plugins/Todos/Todos'
import { history } from 'prosemirror-history'
import { keymap } from 'prosemirror-keymap'
import { Schema } from 'prosemirror-model'
import { QueryClient } from 'react-query'
import { type DocStore } from 'stores/DocStore'
import { Review } from 'stores/Review'
import { TrackedChanges } from 'stores/TrackedChanges'
import { ArticleFlat, Auth, Layer } from 'types'
import { AlmanacDropCursorPlugin } from '../AlmanacDropCursor/AlmanacDropCursor'
import { collabItemsExtractor } from '../CollabItemsExtractor/CollabItemsExtractor'
import { FocusModePlugin } from '../FocusMode'
import { InlineFormatBarPlugin } from '../InlineFormatBarPlugin/InlineFormatBarPlugin'
import { review } from '../Review/Review'
import { TableRightClickMenu } from '../TableRightClickMenu/TableRightClickMenu'
import { TemplatePickerPlugin } from '../TemplatePickerPlugin'
import { trackChanges } from '../TrackChanges/TrackChanges'
import { IStackManager } from 'almanac-ui/Modal/Types'
import { collabItemWidget } from 'common/Prose/Plugins/CollabItemWidget/CollabItemWidget'
import { State } from 'common/Prose/Plugins/CollabItemsExtractor/Types'
import { PredictAIPlugin } from 'proseShared/plugins/PredictAI/PredictAI'
import { PluginKey } from 'prosemirror-state'
import { AuthStore } from 'stores/AuthStore'
import { CaretOnClickOutside } from '../CaretOnClickOutside/CaretOnClickOutside'
import { ContextualBlockActionsPlugin } from '../ContextualBlockActions/ContextualBlockActions'
import { TableDragManagerPlugin } from '../TableDragManager/TableDragManager'
import { stopErroneousKeyPropagation } from '../stopKeyPropagations'
import { StreamDocContentPlugin } from 'common/Prose/Plugins/StreamDocContent/StreamDocContent'
import { AIInlinePromptPlugin } from '../AIInlinePrompt/AIInlinePrompt'
import { AIPromptBarPlugin } from '../AIPromptBar/AIPromptBar'
import { AIRevision } from 'common/Prose/Plugins/AIRevision/AIRevision'

export type PluginsStores = {
  trackChanges?: TrackedChanges
  review?: Review
  docStore?: DocStore
}

export interface EditPluginParams {
  auth?: Auth | null
  authStore?: AuthStore | null
  schema: Schema
  queryClient: QueryClient
  article?: ArticleFlat
  commentOnly?: boolean
  readOnly?: boolean
  isMobile?: boolean
  layer?: Layer
  features?: string[] | undefined
  stores?: PluginsStores
  stackManager: IStackManager
}

export const getEditPlugins = ({
  schema,
  queryClient,
  article,
  commentOnly,
  readOnly,
  isMobile,
  layer,
  features,
  stores = {},
  stackManager,
}: EditPluginParams) => {
  const basePlugins = [stopErroneousKeyPropagation, history()]
  basePlugins.push(
    gapCursor(),
    keymap(Keymap),
    InputRules(schema, !!isMobile),
    PasteImagePlugin(schema.nodes.image),
    PastePlugin({ rootStore: stores.docStore?.rootStore, queryClient }),
    tableSelection(schema),
    TableRightClickMenu(),
    ExtendSelection([
      schema.nodes.list_item,
      {
        type: schema.nodes.box,
        forceCloseSlice: true,
      },
      schema.nodes.summary,
    ]),
    ListItemMarkerSelection,
    SnippetsPlugin(queryClient),
    CoerceLists,
    OpenLinkInNewTab,
    InlineLinkMenuPlugin({
      article,
      layer,
    }),
    EmojiInputPlugin,
    KeepMarks,
    BlockIdPlugin,
    StreamDocContentPlugin,
  )

  if (article && layer && stackManager && stores.docStore) {
    basePlugins.push(
      AIPromptBarPlugin({
        stackManager,
        articleId: article.id,
        channel: article.channel,
        layerId: layer.id,
        queryClient,
        docStore: stores.docStore,
      }),
    )
    basePlugins.push(AIRevision({ queryClient, articleId: article.id, layerId: layer.id, stackManager }))
  }

  basePlugins.push(
    FakeSelection,
    JoinLists,
    ScrollToHashOnSyncFinished,
    SpreadPasteIntoListItems,
    PreserveListItemOnPaste,
    AutoJoinLists,
    InclusiveCodeMarks,
    InlineCodeCursor,
    CleanUpZeroWidthSpaces,
    CaretOnClickOutside,
    PredictAIPlugin,
  )

  if (stackManager && article && layer && stores?.docStore) {
    basePlugins.push(
      AIInlinePromptPlugin({
        article,
        layer,
        queryClient,
      }),
    )
  }

  const notMobile = !isMobile
  if (notMobile) {
    basePlugins.push(BorderFormatBarPlugin, TableFormatBarPlugin, CellFormatBarPlugin, TemplatePickerPlugin)
  }

  if (!layer || !article) {
    basePlugins.push(FocusModePlugin())
  }

  if (!readOnly && !commentOnly) {
    basePlugins.push(ContextualBlockActionsPlugin)

    if (notMobile) {
      basePlugins.push(AlmanacDropCursorPlugin)

      if (stackManager) {
        basePlugins.push(TableDragManagerPlugin(stackManager))
      }
    }
  }

  if (article && layer) {
    basePlugins.push(
      MentionPromptPlugin({
        allowCreateNewFile: true,
        mentioningArticle: article,
        suggestRecentlyReadArticle: true,
      }),
      CommentsPlugin({
        docStore: stores.docStore,
        commentsStore: stores.docStore,
        queryClient,
        enabled: !readOnly,
      }),
      TodosPlugin({ docStore: stores.docStore, queryClient, enabled: !readOnly }),
      MentionsPlugin(article),
      ElementPresencePlugin,
      InlinePresencePlugin(layer.id),
    )
    stores.docStore &&
      basePlugins.push(
        InlineFormatBarPlugin({
          article: article,
          layer: layer,
          queryClient: queryClient,
          inCommentMode: commentOnly,
          docStore: stores.docStore,
          stackManager: stackManager,
        }),
      )

    if (notMobile) {
      basePlugins.push(FloatingMenuAnchorPlugin)
    }

    if (article.main_layer.id === layer.id) {
      basePlugins.push(FocusModePlugin())
    }

    if (stores.trackChanges) {
      basePlugins.push(trackChanges({ store: stores.trackChanges, queryClient }))
      basePlugins.push(collabItemWidget({ store: stores.trackChanges, queryClient }))
      const key = new PluginKey<State>('trackedChangesCollabItemsExtractor')
      basePlugins.push(collabItemsExtractor({ store: stores.trackChanges.collabItemsStore, key }))
    }

    if (stores.review) {
      basePlugins.push(review({ store: stores.review }))
    }

    if (stores.docStore) {
      const key = new PluginKey<State>('collabItemsExtractor')
      basePlugins.push(collabItemsExtractor({ store: stores.docStore.collabItemsStore, key }))
    }
  }

  if (!readOnly && !commentOnly && notMobile) {
    basePlugins.push(EmptyPrompt({ inlineCompletion: features?.includes('ai_inline_completion') }))
  }

  return basePlugins
}
